import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, useQuery, } from '@apollo/client';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import CursorPagination from '../r2/CursorPagination';
import { getDeliveryKey, getDeliveryUrl } from '../../lib/util';
import { classNames } from 'assets/utils/helper';
import { GET_CONTACT_CARDS, GET_CONTACT_CARDS_BY_CONTACT_CARD_CATEGORY, } from '../r2/graphql/GET_CONTACT_CARDS';
import { useRouter } from 'next/router';
import PlaceholderImage from '../../assets/images/oncall-profile-card-placeholder-image.png';

const CONTACT_CARDS_ROOT = '10993545-CB62-421C-81A9-7A2AA2DA51AF';
const PAGE_SIZE = 12;
const EMPLOYER_SPECIALIST_ID = 'CE033B49-7BDE-45C9-BC1B-1503D23B7D8E';
const PROVIDER_SPECIALIST_ID = 'A71EB570-8CCF-4A2C-8EFF-A61BB6754CF5';
const CONTACT_CARD_TEMPLATE_ID = '2B921664-7126-4008-A2B0-2681DB5B6DD6';
const CARDS_SCROLL_OFFSET = 120;
const deliveryKey = getDeliveryKey();
const endpoint = getDeliveryUrl();

const client = new ApolloClient({
  link: new HttpLink({
    uri: endpoint,
    headers: { sc_apikey: deliveryKey },
  }),
  cache: new InMemoryCache(),
});

type SelectedContactCardCategory = 'all' | 'provider' | 'employer';

type GraphQLField = {
  value?: string;
  jsonValue?: any;
};

type ContactCardResult = {
  id: string;
  path: string;
  Image?: GraphQLField;
  Title?: GraphQLField;
  Description?: GraphQLField;
  ContactInformation?: GraphQLField;
  ContactIcon?: GraphQLField;
  Phone?: GraphQLField;
  PhoneIcon?: GraphQLField;
  Email?: GraphQLField;
  EmailIcon?: GraphQLField;
  CTA?: GraphQLField;
  CTAType?: GraphQLField;
  BackgroundColor?: GraphQLField;
  ContactCardCategories?: GraphQLField;
};

const getImageSrc = (field?: GraphQLField): string => {
  return (
    field?.jsonValue?.value?.src ||
    field?.jsonValue?.src ||
    field?.value ||
    ''
  );
};

const getFallbackProfileImageSrc = (): string => {
  return typeof PlaceholderImage === 'string' ? PlaceholderImage : PlaceholderImage.src;
};

const getImageAlt = (field?: GraphQLField, fallback = 'Image'): string => {
  return field?.jsonValue?.value?.alt || field?.jsonValue?.alt || fallback;
};

const getLinkHref = (field?: GraphQLField): string => {
  return field?.jsonValue?.value?.href || field?.jsonValue?.href || '';
};

const getLinkText = (field?: GraphQLField): string => {
  return (
    field?.jsonValue?.value?.text ||
    field?.jsonValue?.text ||
    field?.value ||
    ''
  );
};

const getDroplinkText = (field?: GraphQLField): string => {
  return (
    field?.jsonValue?.fields?.heading?.value ||
    field?.jsonValue?.name ||
    field?.jsonValue?.displayName ||
    field?.value ||
    ''
  );
};

const toClassNameValue = (value: string): string => {
  return value.toLowerCase().trim().replace(/\s+/g, '-');
};

const getContactCardCategoryFromUrl = (
  contactCardCategory?: string | string[]
): SelectedContactCardCategory => {
  const value = Array.isArray(contactCardCategory)
    ? contactCardCategory[0]
    : contactCardCategory;

  if (!value) return 'all';

  const normalizedValue = decodeURIComponent(value).toLowerCase().trim();

  if (normalizedValue === 'provider specialists') return 'provider';
  if (normalizedValue === 'employer specialists') return 'employer';

  return 'all';
};

const getContactCardCategoryUrlValue = (
  contactCardCategory: SelectedContactCardCategory
): string => {
  if (contactCardCategory === 'provider') return 'Provider Specialists';
  if (contactCardCategory === 'employer') return 'Employer Specialists';

  return '';
};

const updateUrlWithoutNextScroll = (
  contactCardCategory: SelectedContactCardCategory
) => {
  if (typeof window === 'undefined') return;

  const basePath = window.location.pathname;
  let nextUrl = basePath;

  if (contactCardCategory !== 'all') {
    const contactCardCategoryValue =
      getContactCardCategoryUrlValue(contactCardCategory);

    nextUrl = `${basePath}?contactCardCategory=${encodeURIComponent(
      contactCardCategoryValue
    )}`;
  }

  window.history.replaceState(
    {
      ...(window.history.state || {}),
      as: nextUrl,
      url: nextUrl,
    },
    '',
    nextUrl
  );
};

const ContactCardListing = (props: any): JSX.Element => {
  const router = useRouter();

  const cardWrapperRef = useRef<HTMLDivElement | null>(null);
  const cardsTopRef = useRef<HTMLDivElement | null>(null);
  const filterScrollYRef = useRef<number | null>(null);

  const [selectedContactCardCategory, setSelectedContactCardCategory] =
    useState<SelectedContactCardCategory>('all');
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<string[]>(['']);
  const [jumpLoading, setJumpLoading] = useState(false);
  const [visibleData, setVisibleData] = useState<any>(null);

  const scrollToCards = () => {
    if (typeof window === 'undefined') return;

    const element = cardsTopRef.current;

    if (!element) return;

    const elementTop = element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: Math.max(elementTop - CARDS_SCROLL_OFFSET, 0),
      behavior: 'smooth',
    });
  };

  const preserveCurrentScrollPosition = () => {
    if (typeof window === 'undefined') return;

    filterScrollYRef.current = window.scrollY;
  };

  const restoreFilterScrollPosition = () => {
    if (typeof window === 'undefined') return;
    if (filterScrollYRef.current === null) return;

    const scrollY = filterScrollYRef.current;
    filterScrollYRef.current = null;

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollY,
        left: 0,
        behavior: 'auto',
      });

      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollY,
          left: 0,
          behavior: 'auto',
        });
      });
    });
  };

  useEffect(() => {
    if (!router.isReady) return;

    const contactCardCategoryFromUrl = getContactCardCategoryFromUrl(
      router.query.contactCardCategory
    );

    setSelectedContactCardCategory(prev =>
      prev === contactCardCategoryFromUrl ? prev : contactCardCategoryFromUrl
    );
  }, [router.isReady, router.query.contactCardCategory]);

  const contactCardCategoryId = useMemo(() => {
    if (selectedContactCardCategory === 'provider') {
      return PROVIDER_SPECIALIST_ID;
    }

    if (selectedContactCardCategory === 'employer') {
      return EMPLOYER_SPECIALIST_ID;
    }

    return '';
  }, [selectedContactCardCategory]);

  const activeQuery =
    selectedContactCardCategory === 'all'
      ? GET_CONTACT_CARDS
      : GET_CONTACT_CARDS_BY_CONTACT_CARD_CATEGORY;

  const getQueryVariables = (after: string | null) => {
    const baseVariables = {
      path: CONTACT_CARDS_ROOT,
      templateId: CONTACT_CARD_TEMPLATE_ID,
      pageSize: PAGE_SIZE,
      after,
    };

    if (selectedContactCardCategory === 'all') {
      return baseVariables;
    }

    return {
      ...baseVariables,
      contactCardCategoryId,
    };
  };

  const variables = getQueryVariables(endCursor);

  const { data, loading, error } = useQuery(activeQuery, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Reset cursor pagination whenever selected category changes.
  useEffect(() => {
    setEndCursor(null);
    setCursorHistory(['']);
    setPage(1);
  }, [selectedContactCardCategory]);

  useEffect(() => {
    if (data?.search) {
      setVisibleData(data);
    }
  }, [data]);

  useEffect(() => {
    if (!loading && !jumpLoading && visibleData) {
      restoreFilterScrollPosition();
    }
  }, [loading, jumpLoading, visibleData]);

  useEffect(() => {
    if (error) {
      console.error(
        'Contact cards query error:',
        JSON.stringify(error, null, 2)
      );
    }
  }, [error]);

  const rawContactCards: ContactCardResult[] =
    visibleData?.search?.results || [];

  const contactCards = rawContactCards.filter(card => {
    return (
      card.Title?.value ||
      getImageSrc(card.Image) ||
      card.Description?.value ||
      card.ContactInformation?.value ||
      card.Phone?.value ||
      card.Email?.value ||
      getLinkHref(card.CTA)
    );
  });

  const showImageSection = contactCards.length > 0;
  const showTitleSection = contactCards.some(card => card.Title?.value);
  const showDescriptionSection = contactCards.some(
    card => card.Description?.value
  );

  const showContactInformationRow = contactCards.some(
    card => card.ContactInformation?.value
  );
  const showPhoneRow = contactCards.some(card => card.Phone?.value);
  const showEmailRow = contactCards.some(card => card.Email?.value);

  const showDetailsSection =
    showContactInformationRow || showPhoneRow || showEmailRow;

  const showButtonSection = contactCards.some(card => getLinkHref(card.CTA));

  const contactCardsKey = contactCards.map(card => card.id).join('|');

  useEffect(() => {
    if (!cardWrapperRef.current || loading || jumpLoading) return;

    const wrapper = cardWrapperRef.current;

    const sectionClassNames = [
      'contact-card-new-image-slot',
      'contact-card-new-heading',
      'contact-card-new-description',
      'contact-card-new-contact-information-row',
      'contact-card-new-phone-row',
      'contact-card-new-email-row',
      'contact-card-new-button',
    ];

    const resetSectionStyles = () => {
      sectionClassNames.forEach(sectionClassName => {
        const elements = Array.from(
          wrapper.querySelectorAll<HTMLElement>(`.${sectionClassName}`)
        );

        elements.forEach(element => {
          element.style.minHeight = '';
          element.style.display = '';
        });
      });
    };

    const getCardsGroupedByRow = () => {
      const cards = Array.from(
        wrapper.querySelectorAll<HTMLElement>('.contact-card-new-card')
      );

      const rows: HTMLElement[][] = [];

      cards.forEach(card => {
        const cardTop = Math.round(card.getBoundingClientRect().top);

        const matchingRow = rows.find(row => {
          const rowTop = Math.round(row[0].getBoundingClientRect().top);

          return Math.abs(rowTop - cardTop) <= 2;
        });

        if (matchingRow) {
          matchingRow.push(card);
        } else {
          rows.push([card]);
        }
      });

      return rows;
    };

    const syncCardSectionHeightsByRow = () => {
      resetSectionStyles();

      const rows = getCardsGroupedByRow();

      rows.forEach(rowCards => {
        sectionClassNames.forEach(sectionClassName => {
          const sectionElements = rowCards
            .map(card => card.querySelector<HTMLElement>(`.${sectionClassName}`))
            .filter(Boolean) as HTMLElement[];

          if (!sectionElements.length) return;

          const maxHeight = Math.max(
            ...sectionElements.map(element => element.offsetHeight)
          );

          sectionElements.forEach(element => {
            if (maxHeight > 0) {
              element.style.minHeight = `${maxHeight}px`;
            } else {
              element.style.display = 'none';
            }
          });
        });
      });
    };

    let animationFrameId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(syncCardSectionHeightsByRow);
    };

    const resizeObserver = new ResizeObserver(scheduleSync);

    resizeObserver.observe(wrapper);

    const images = Array.from(wrapper.querySelectorAll('img'));

    images.forEach(image => {
      image.addEventListener('load', scheduleSync);
    });

    window.addEventListener('resize', scheduleSync);

    scheduleSync();

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();

      images.forEach(image => {
        image.removeEventListener('load', scheduleSync);
      });

      window.removeEventListener('resize', scheduleSync);
    };
  }, [
    loading,
    jumpLoading,
    page,
    selectedContactCardCategory,
    contactCardsKey,
  ]);

  const total = visibleData?.search?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const safePage = total > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
  const startIndex = total > 0 ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const endIndex = total > 0 ? Math.min(safePage * PAGE_SIZE, total) : 0;
  const hasPagination = total > PAGE_SIZE;

  const handleContactCardCategoryChange = (
    contactCardCategory: SelectedContactCardCategory
  ) => {
    if (contactCardCategory === selectedContactCardCategory) return;

    preserveCurrentScrollPosition();

    setSelectedContactCardCategory(contactCardCategory);
    updateUrlWithoutNextScroll(contactCardCategory);
  };

  const handleNext = () => {
    const nextCursor = data?.search?.pageInfo?.endCursor;

    if (nextCursor) {
      scrollToCards();
      setCursorHistory(prev => [...prev, nextCursor]);
      setEndCursor(nextCursor);
      setPage(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (cursorHistory.length > 1) {
      scrollToCards();
      const newHistory = [...cursorHistory];
      newHistory.pop();

      const prevCursor = newHistory[newHistory.length - 1] || null;

      setCursorHistory(newHistory);
      setEndCursor(prevCursor);
      setPage(prev => Math.max(prev - 1, 1));
    }
  };

  const handlePageClick = async (targetPage: number) => {
    if (targetPage === page) return;
    scrollToCards();

    if (targetPage < page) {
      const newHistory = cursorHistory.slice(0, targetPage);
      const newCursor = newHistory[newHistory.length - 1] || null;

      setCursorHistory(newHistory);
      setEndCursor(newCursor);
      setPage(targetPage);
      return;
    }

    setJumpLoading(true);

    try {
      const newHistory = [...cursorHistory];
      let tempCursor = endCursor;

      // Cursor pagination cannot jump directly, so walk forward to the target page.
      for (let i = page; i < targetPage; i++) {
        const result = await client.query({
          query: activeQuery,
          variables: getQueryVariables(tempCursor),
          fetchPolicy: 'network-only',
        });

        tempCursor = result.data?.search?.pageInfo?.endCursor;

        if (tempCursor) {
          newHistory.push(tempCursor);
        } else {
          break;
        }
      }

      setCursorHistory(newHistory);
      setEndCursor(newHistory[targetPage - 1] || null);
      setPage(targetPage);
    } finally {
      setJumpLoading(false);
    }
  };

  if (error && !visibleData) {
    return <div className="text-center">Unable to load contact cards.</div>;
  }

  if (loading && !visibleData) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <div
      className="overhauled"
      id={`contact-card-new-${props.rendering?.uid}`}
    >
      <div
        className={classNames(
          'contact-card-new',
          !hasPagination ? 'no-pagination' : ''
        )}
      >
        <div className="jss-inner-container">
          <div className="contact-card-new-filter-row">
            <span className="contact-card-new-filter-label">Sort By:</span>

            <div className="contact-card-new-pill-list">
              <button
                type="button"
                className={classNames(
                  'contact-card-new-pill',
                  selectedContactCardCategory === 'all' ? 'is-active' : ''
                )}
                aria-pressed={selectedContactCardCategory === 'all'}
                onClick={() => handleContactCardCategoryChange('all')}
              >
                All
              </button>

              <button
                type="button"
                className={classNames(
                  'contact-card-new-pill',
                  selectedContactCardCategory === 'provider' ? 'is-active' : ''
                )}
                aria-pressed={selectedContactCardCategory === 'provider'}
                onClick={() => handleContactCardCategoryChange('provider')}
              >
                Provider Specialists
              </button>

              <button
                type="button"
                className={classNames(
                  'contact-card-new-pill',
                  selectedContactCardCategory === 'employer' ? 'is-active' : ''
                )}
                aria-pressed={selectedContactCardCategory === 'employer'}
                onClick={() => handleContactCardCategoryChange('employer')}
              >
                Employer Specialists
              </button>
            </div>
          </div>

          <div
            ref={cardsTopRef}
            className="contact-card-new-scroll-anchor"
            aria-hidden="true"
          />

          {contactCards.length > 0 ? (
            <div className="contact-card-new-wrapper" ref={cardWrapperRef}>
              {contactCards.map((card, index) => {
                const sitecoreImageSrc = getImageSrc(card.Image);
                const imageSrc = sitecoreImageSrc || getFallbackProfileImageSrc();
                const imageAlt = sitecoreImageSrc
                  ? getImageAlt(card.Image, card.Title?.value || 'Image')
                  : 'Profile image placeholder';

                const contactIconSrc = getImageSrc(card.ContactIcon);
                const contactIconAlt = getImageAlt(card.ContactIcon, '');

                const phoneIconSrc = getImageSrc(card.PhoneIcon);
                const phoneIconAlt = getImageAlt(card.PhoneIcon, '');

                const emailIconSrc = getImageSrc(card.EmailIcon);
                const emailIconAlt = getImageAlt(card.EmailIcon, '');

                const ctaHref = getLinkHref(card.CTA);
                const ctaText = getLinkText(card.CTA);

                const ctaType = getDroplinkText(card.CTAType) || 'primary';
                const ctaClass = `btn-${toClassNameValue(ctaType)}`;

                const backgroundColor = getDroplinkText(card.BackgroundColor);
                const backgroundClass = backgroundColor
                  ? `bg-${toClassNameValue(backgroundColor)}`
                  : '';

                return (
                  <div
                    key={card.id}
                    className="groupings-anim animated"
                    style={{ '--index': index + 1 } as React.CSSProperties}
                  >
                    <div
                      className={classNames(
                        'contact-card-new-card inner-side-spacing',
                        backgroundClass
                      )}
                    >
                      <div className="contact-card-new-content">
                        {showImageSection && (
                          <div className="contact-card-new-image-slot">
                            <div className="contact-card-new-image">
                              <img src={imageSrc} alt={imageAlt} />
                            </div>
                          </div>
                        )}

                        {showTitleSection && (
                          <div className="contact-card-new-heading">
                            {card.Title?.value && (
                              <h3 className="osf-display-h3 header">
                                {card.Title.value}
                              </h3>
                            )}
                          </div>
                        )}

                        {showDescriptionSection && (
                          <div className="contact-card-new-description">
                            {card.Description?.value && (
                              <div
                                className="description osf-body-copy"
                                dangerouslySetInnerHTML={{
                                  __html: card.Description.value,
                                }}
                              />
                            )}
                          </div>
                        )}

                        {showDetailsSection && (
                          <div className="contact-card-new-details">
                            {showContactInformationRow && (
                              <div className="contact-card-new-detail-row contact-card-new-contact-information-row">
                                {card.ContactInformation?.value && (
                                  <>
                                    {contactIconSrc && (
                                      <img
                                        className="contact-card-new-icon"
                                        src={contactIconSrc}
                                        alt={contactIconAlt}
                                      />
                                    )}

                                    <p className="osf-body-copy">
                                      {card.ContactInformation.value}
                                    </p>
                                  </>
                                )}
                              </div>
                            )}

                            {showPhoneRow && (
                              <div className="contact-card-new-detail-row contact-card-new-phone-row">
                                {card.Phone?.value && (
                                  <>
                                    {phoneIconSrc && (
                                      <img
                                        className="contact-card-new-icon"
                                        src={phoneIconSrc}
                                        alt={phoneIconAlt}
                                      />
                                    )}

                                    <a href={`tel:${card.Phone.value}`}>
                                      {card.Phone.value}
                                    </a>
                                  </>
                                )}
                              </div>
                            )}

                            {showEmailRow && (
                              <div className="contact-card-new-detail-row contact-card-new-email-row">
                                {card.Email?.value && (
                                  <>
                                    {emailIconSrc && (
                                      <img
                                        className="contact-card-new-icon"
                                        src={emailIconSrc}
                                        alt={emailIconAlt}
                                      />
                                    )}

                                    <a href={`mailto:${card.Email.value}`}>
                                      {card.Email.value}
                                    </a>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {showButtonSection && (
                          <div className="contact-card-new-button">
                            {ctaHref && (
                              <a
                                href={ctaHref}
                                className={classNames(
                                  'btn-mobile-fw',
                                  ctaClass
                                )}
                              >
                                {ctaText || 'Learn More'}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="contact-card-new-empty">No contact cards found.</div>
          )}

          {loading && visibleData && (
            <div className="contact-card-new-loading" aria-live="polite">
              Loading...
            </div>
          )}

          {hasPagination && (
            <div className="pagination-container">
              <CursorPagination
                onNext={handleNext}
                onPrevious={handlePrevious}
                hasNext={visibleData?.search?.pageInfo?.hasNext}
                page={page}
                totalPages={totalPages}
                onPageClick={handlePageClick}
              />

              <div className="result-count" aria-live="polite">
                {`Showing ${startIndex} - ${endIndex} of ${total}`}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WrappedContactCardListing = (props: any) => (
  <ApolloProvider client={client}>
    <ContactCardListing {...props} />
  </ApolloProvider>
);

export default withSitecoreContext()(WrappedContactCardListing);